import React from 'react';
import styles from './Avatar.module.css';

export type Attendance = 'going' | 'pending' | 'declined';
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

export interface Person {
    userNo: number | string;
    /** 표시 이름 — 이미지가 없을 때 첫 글자를 씁니다 */
    name: string;
    imageUrl?: string | null;
    attendance?: Attendance;
    isHost?: boolean;
}

interface AvatarProps {
    person: Person;
    size?: AvatarSize;
    /** 상태 텍스트 — 스크린리더용. i18n 으로 넘깁니다 (예: "참여") */
    statusLabel?: string;
}

/**
 * 참여 상태를 색이 아니라 형태로 표시합니다.
 *   참여 = 실선 테두리 · 미정 = 점선 · 불참 = 흐림 + 사선
 * 색각 이상 사용자도 구분할 수 있고, 팔레트를 늘리지 않습니다.
 */
export const Avatar = ({ person, size = 'md', statusLabel }: AvatarProps) => {
    const att = person.attendance || 'pending';
    const cls = [
        styles.avatar,
        styles[size],
        styles[att],
        person.isHost ? styles.host : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <span
            className={cls}
            title={statusLabel ? `${person.name} · ${statusLabel}` : person.name}
            aria-label={statusLabel ? `${person.name} ${statusLabel}` : person.name}
        >
            {person.imageUrl ? (
                <img className={styles.img} src={person.imageUrl} alt="" />
            ) : (
                person.name.slice(0, 1)
            )}
        </span>
    );
};

interface AvatarStackProps {
    people: Person[];
    /** 몇 명까지 보여줄지 (기본 4) */
    max?: number;
    size?: AvatarSize;
    /** 상태별 라벨 — i18n 으로 넘깁니다 */
    labels?: Partial<Record<Attendance, string>>;
}

const REST_SIZE: Record<AvatarSize, number> = { xs: 20, sm: 28, md: 36, lg: 48 };

export const AvatarStack = ({ people, max = 4, size = 'sm', labels }: AvatarStackProps) => {
    const shown = people.slice(0, max);
    const rest = people.length - shown.length;
    const px = REST_SIZE[size];

    return (
        <span className={styles.stack}>
            {shown.map((p) => (
                <Avatar
                    key={p.userNo}
                    person={p}
                    size={size}
                    statusLabel={labels?.[p.attendance || 'pending']}
                />
            ))}
            {rest > 0 && (
                <span className={styles.rest} style={{ width: px, height: px }}>
                    +{rest}
                </span>
            )}
        </span>
    );
};

interface AttendanceLegendProps {
    /** i18n 라벨 3종 */
    labels: Record<Attendance, string>;
}

/** 형태로 구분한다는 규칙을 사용자에게 알려주는 범례. 참여자 목록 상단에 한 번만. */
export const AttendanceLegend = ({ labels }: AttendanceLegendProps) => (
    <div className={styles.legend}>
        <span className={styles.legendItem}>
            <i className={styles.legendMark} style={{ border: '2px solid var(--t-ink-1)' }} aria-hidden />
            {labels.going}
        </span>
        <span className={styles.legendItem}>
            <i className={styles.legendMark} style={{ border: '2px dashed var(--t-line-strong)' }} aria-hidden />
            {labels.pending}
        </span>
        <span className={styles.legendItem}>
            <i className={styles.legendMark} style={{ border: '2px solid var(--t-line)', opacity: 0.45 }} aria-hidden />
            {labels.declined}
        </span>
    </div>
);
